import moment from 'moment';
import { useSelector } from 'react-redux';

// A device that has stopped reporting keeps serving its last known readings, so a
// tracker silent for months still shows the voltage it saw on its final fix. That
// reads as a live gauge saying "the battery is fine", which is how anyone glancing
// at the list will take it. Past the timeout the reading is no longer a statement
// about the device now, and callers strike it through instead.
//
// No lastUpdate at all means the server has never heard from the device, so
// nothing shown beside it can be a current reading either. A null timeout means
// the server configuration has not loaded yet, and nothing is claimed until it has.
export const isStale = (lastUpdate, timeout) => {
  if (!timeout) {
    return false;
  }
  if (!lastUpdate) {
    return true;
  }
  return moment().diff(moment(lastUpdate), 'seconds', true) > timeout;
};

// GET /api/server carries staleTimeout in seconds. It is server configuration
// rather than a preference - read-only, always present - so it is read straight
// off the session instead of going through useAttributePreference. Null until the
// session loads; there is deliberately no local default, so what the interface
// treats as silent can never disagree with what the server is enforcing.
export const useStaleTimeout = () => useSelector((state) => {
  const timeout = Number(state.session.server?.staleTimeout);
  return Number.isFinite(timeout) && timeout > 0 ? timeout : null;
});

// Returns the props every indicator in CommonObjects takes. Callers must pass a
// device: lastUpdate is the server's record of when it last made contact, and is
// what the "3 months ago" text beside these icons already reports.
export const useStale = (device) => {
  const timeout = useStaleTimeout();
  const lastUpdate = device?.lastUpdate || null;
  return { stale: isStale(lastUpdate, timeout), staleSince: lastUpdate };
};
